import { APP_JSON, NOT_ACCEPTED } from '../../constants/common-enums';
import fetchReq from '../../helpers/fetch';
import getIntentPayload from '../../helpers/get-intent-payload';
import GtmHelper from '../../helpers/gtm';
import isEmptyString from '../../helpers/is-empty-string';
import DcfLaunchData from '../../lib/dcf/launch-data';
import CONFIG from '../../settings';

const API = {
  cancelIntent(accessToken, apiKey, vIntentId) {
    const cancelIntentUri = `${
      CONFIG.application.api.purchaseIntent
    }/${vIntentId}?apikey=${apiKey}`;
    /* Cancel Intent - Wallet Service **/
    const options = {
      headers: {
        Accept: APP_JSON,
        'Content-Type': APP_JSON,
        access_token: accessToken
      },
      method: 'DELETE',
      url: cancelIntentUri
    };

    return fetchReq(options);
  },
  /**
   * Get card list based on vIntentId
   * - Only get the users card list if they have accepted TnC
   * - [present], Invoke Visa BA GetIntent API(to fetch card lists) if intent id already exists
   * - [null/undefined], Invoke Visa BA SetIntent API(to fetch card lists)
   * - returns http response object
   * - TODO Unit test script
   * @param {object} object bag of the following:
   * - {string} accessToken
   * - {boolean} isLocked if user account or device is locked or null
   * - {object} merchantInfo
   * - {boolean} tncStatus does user need to accept TnC
   * - {boolean} trackSetIntent if true GTM event will fire
   * @returns {object} response object
   */
  getCardList({
    accessToken,
    isLocked,
    merchantInfo,
    tncStatus,
    trackSetIntent = false,
    isVisaSrci
  }) {
    const { vIntentId, apikey } = merchantInfo;
    if (tncStatus === NOT_ACCEPTED || isLocked) {
      // Caller of `getCardList` expects a promise to be returned
      return Promise.resolve({ ok: true, paymentInstrument: [] });
    } else if (vIntentId) {
      return this.getIntent(accessToken, apikey, vIntentId);
    }
    return this.setIntent(accessToken, merchantInfo, trackSetIntent, isVisaSrci);
  },
  /**
   * Get PreCheckout data/Card list API
   * - Invoke Visa BA GetIntent API(to fetch card lists) if intent id already exists
   * - returns http response object
   * - TODO Unit test script
   * @param {string} accessToken
   * @param {string} apiKey
   * @param {string} vIntentId
   * @returns {object} response object
   */
  getIntent(accessToken, apiKey, vIntentId) {
    // Default to true if the showVdcpIno is not found.
    const showVdcpIno = DcfLaunchData.customProps?.showVdcpIno ?? true;
    // if showVdcpIno is true, getIntent returns only visa cards.
    const getIntentUri = `${
      CONFIG.application.api.intent
    }/${vIntentId}?apikey=${apiKey}&context=srcProfile&visaCardsOnly=${showVdcpIno}&isInstallmentSupported=true`;
    /* Get Intent - Wallet Service **/
    const options = {
      headers: {
        Accept: APP_JSON,
        'Content-Type': APP_JSON,
        access_token: accessToken
      },
      method: 'GET',
      url: getIntentUri
    };

    return fetchReq(options);
  },

  /**
   * Purchase intent, to complete transaction with given card
   * Used by system-sdk-external authorizeTransaction() PSP ssi/erm enroll card
   * @param {object} xhr and payment info data
   * @returns {object} response object
   */
  purchaseIntent({ accessToken, apiKey, paymentInfoData = {}, vIntentId }) {
    const requestPayload = {};

    requestPayload.referenceURL = window.location.href || '';
    if (!isEmptyString(paymentInfoData.selectedCardId)) {
      requestPayload.paymentInstrumentId = paymentInfoData.selectedCardId;
    }
    if (!isEmptyString(paymentInfoData.shippingAddressId)) {
      requestPayload.shippingAddressId = paymentInfoData.shippingAddressId;
    }

    if (paymentInfoData.encPaymentInstrument) {
      requestPayload.encPaymentInstrument = paymentInfoData.encPaymentInstrument;
    }
    if (paymentInfoData.cvv) {
      requestPayload.cvv = paymentInfoData.cvv;
    }
    if (!isEmptyString(paymentInfoData.sessionId)) {
      requestPayload.sessionId = paymentInfoData.sessionId;
    }
    // this is required if CVM is shown
    if (
      !isEmptyString(paymentInfoData.cvmValue) &&
      paymentInfoData.cvmValue &&
      /^\d+$/.test(paymentInfoData.cvmValue)
    ) {
      requestPayload.cvmVerificationValue = paymentInfoData.cvmValue;
    }
    // ComboCard type for SSI/ERM users, if modified
    if (!isEmptyString(paymentInfoData.comboCardType)) {
      requestPayload.paymentInstrumentType = paymentInfoData.comboCardType;
    }
    // if srciTransactionId is present
    if (!isEmptyString(paymentInfoData.srciTransactionId)) {
      requestPayload.srciTransactionId = paymentInfoData.srciTransactionId;
    }
    // if paResPayload is present
    if (typeof paymentInfoData.paResPayload === 'string') {
      requestPayload.paResPayload = paymentInfoData.paResPayload;
      requestPayload.isThreeDSVerificationResponse = paymentInfoData.isThreeDSVerificationResponse;
    }
    const options = {
      body: requestPayload,
      headers: {
        Accept: APP_JSON,
        'Content-Type': APP_JSON,
        access_token: accessToken
      },
      method: 'PUT',
      url: `${CONFIG.application.api.purchaseIntent}/${vIntentId}?apikey=${apiKey}`
    };
    return fetchReq(options);
  },

  /**
   * Get PreCheckout data/Card list API
   * - Invoke Visa BA SetIntent API(to fetch card lists)
   * - returns http response object
   * - TODO Unit test script
   * @param {string} accessToken
   * @param {object} merchantInfo
   * @param {boolean} trackSetIntent if true GTM event will fire
   * @param {boolean} isVisaSrci
   * @returns {object} response object
   */
  setIntent(accessToken, merchantInfo, trackSetIntent = false, isVisaSrci) {
    if (trackSetIntent) {
      GtmHelper.trackSetIntentCalled(merchantInfo, DcfLaunchData, true, isVisaSrci);
      GtmHelper.trackSetIntentCalled(merchantInfo, DcfLaunchData, false, isVisaSrci);
    }

    // Default to true if the showVdcpIno is not found.
    const showVdcpIno = DcfLaunchData?.customProps?.showVdcpIno ?? true;
    // if showVdcpIno is true, setIntent returns only visa cards.
    const setIntentUri = `${CONFIG.application.api.intent}?apikey=${merchantInfo.apikey}`;
    const requestPayload = getIntentPayload(merchantInfo);
    requestPayload.visaCardsOnly = showVdcpIno;
    /* Set Intent - Wallet Service **/
    const options = {
      body: requestPayload,
      headers: {
        Accept: APP_JSON,
        'Content-Type': APP_JSON,
        access_token: accessToken
      },
      method: 'POST',
      url: setIntentUri
    };

    return fetchReq(options);
  }
};

export default API;
